import { useCallback, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';

import logoSmall from '@/assets/logo-small.png';
import logo from '@/assets/logo.png';
import { useCognitoAuth } from '@/contexts/AuthContext';
import { useOrganization } from '@/hooks/useOrganization';

interface NavItem {
  label: string;
  href: string;
  icon: string;
  end?: boolean;
}

interface NavSection {
  title?: string;
  items: NavItem[];
}

interface SidebarProps {
  collapsed?: boolean;
}

const navSections: NavSection[] = [
  {
    items: [
      { label: 'Dashboard', href: '/app', icon: 'bi-house', end: true },
    ],
  },
  {
    title: 'Accounting',
    items: [
      { label: 'Chart of Accounts', href: '/app/accounts', icon: 'bi-list-ul' },
      { label: 'Journal Entries', href: '/app/journal', icon: 'bi-journal-text' },
      { label: 'Transactions', href: '/app/transactions', icon: 'bi-arrow-left-right' },
      { label: 'Reconciliation', href: '/app/reconciliation', icon: 'bi-check2-square' },
    ],
  },
  {
    title: 'Banking',
    items: [
      { label: 'Bank Accounts', href: '/app/bank-accounts', icon: 'bi-bank' },
    ],
  },
  {
    title: 'Inventory',
    items: [
      { label: 'Inventory Items', href: '/app/inventory', icon: 'bi-box-seam' },
      { label: 'Valuation', href: '/app/reports/inventory-valuation', icon: 'bi-currency-dollar' },
    ],
  },
  {
    title: 'Reports',
    items: [
      { label: 'Trial Balance', href: '/app/reports/trial-balance', icon: 'bi-table' },
      { label: 'Balance Sheet', href: '/app/reports/balance-sheet', icon: 'bi-file-earmark-spreadsheet' },
      { label: 'Profit & Loss', href: '/app/reports/profit-loss', icon: 'bi-graph-up' },
      { label: 'Tax Summary', href: '/app/reports/tax-summary', icon: 'bi-receipt' },
    ],
  },
];

function NavLinkItem({ item, collapsed, exact }: { item: NavItem; collapsed: boolean; exact?: boolean }) {
  const getClassName = ({ isActive }: { isActive: boolean }) =>
    `flex items-center gap-3 px-4 py-2 mx-2 rounded-lg transition-colors ${
      isActive
        ? 'bg-[var(--color-sidebar-primary)] text-white'
        : 'hover:bg-[var(--color-sidebar-accent)]'
    } ${collapsed ? 'justify-center' : ''}`;

  return (
    <NavLink
      to={item.href}
      end={exact ?? item.end}
      className={getClassName}
      title={collapsed ? item.label : undefined}
    >
      <i className={`bi ${item.icon} ${collapsed ? 'text-lg' : ''}`}></i>
      {!collapsed && <span>{item.label}</span>}
    </NavLink>
  );
}

function findActiveSection(pathname: string): number | null {
  for (let i = 0; i < navSections.length; i++) {
    const section = navSections[i];
    if (!section.title) continue;
    for (const item of section.items) {
      if (item.end ? pathname === item.href : pathname.startsWith(item.href)) {
        return i;
      }
    }
  }
  return null;
}

const settingsNavItems: NavItem[] = [
  { label: 'Back', href: '/app', icon: 'bi-arrow-left' },
  { label: 'Profile', href: '/app/settings/profile', icon: 'bi-person' },
];

function SettingsSubNav({ collapsed, orgId }: { collapsed: boolean; orgId?: number }) {
  const location = useLocation();
  const pathParts = location.pathname.split('/');
  const isOrgSettings = location.pathname.startsWith('/app/organizations/') && location.pathname.includes('/settings');
  const resolvedOrgId = isOrgSettings ? Number(pathParts[3]) : orgId;

  const orgSubItems = (isOrgSettings || resolvedOrgId) && resolvedOrgId ? [
    { label: 'Members', href: `/app/organizations/${resolvedOrgId}/settings/members`, icon: 'bi-people' },
    { label: 'Tax Codes', href: `/app/organizations/${resolvedOrgId}/settings/tax-codes`, icon: 'bi-percent' },
    { label: 'GFI Codes', href: `/app/gfi-codes`, icon: 'bi-receipt' },
    { label: 'Integrations', href: `/app/organizations/${resolvedOrgId}/settings/integrations`, icon: 'bi-plug' },
    { label: 'Financial', href: `/app/organizations/${resolvedOrgId}/settings/financial`, icon: 'bi-calendar-check' },
  ] : [];

  return (
    <nav className="flex-1 overflow-y-auto py-4">
      {!collapsed && (
        <div className="px-4 py-2 text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--color-sidebar-foreground)', opacity: 0.5 }}>
          Settings
        </div>
      )}
      {settingsNavItems.map((item) => (
        <NavLinkItem key={item.href} item={item} collapsed={collapsed} exact={item.href === '/app'} />
      ))}
      {orgSubItems.length > 0 && (
        <>
          {!collapsed && (
            <div className="px-4 py-2 text-xs font-semibold uppercase tracking-wider mt-2" style={{ color: 'var(--color-sidebar-foreground)', opacity: 0.5 }}>
              Organization
            </div>
          )}
          {orgSubItems.map((item) => (
            <NavLinkItem key={item.href} item={item} collapsed={collapsed} />
          ))}
        </>
      )}
    </nav>
  );
}

export function Sidebar({ collapsed = false }: SidebarProps) {
  const { user, signOut } = useCognitoAuth();
  const { organization } = useOrganization();
  const navigate = useNavigate();
  const location = useLocation();

  const [userSection, setUserSection] = useState<number | null>(null);

  const autoSection = findActiveSection(location.pathname);
  const expandedSection = userSection ?? autoSection;

  const toggleSection = useCallback((idx: number) => {
    setUserSection((prev) => (prev === idx ? null : idx));
  }, []);

  const isSettingsPage = location.pathname.startsWith('/app/settings') ||
    location.pathname.startsWith('/app/organizations/');

  const handleSignOut = useCallback(() => {
    void signOut();
    void navigate('/login');
  }, [signOut, navigate]);

  return (
    <div className="h-full flex flex-col">
      {/* Brand */}
      <div className={`border-b border-[var(--color-sidebar-border)] ${collapsed ? 'p-3' : 'p-5'}`}>
        <div className={`font-semibold ${collapsed ? 'text-center' : 'flex items-center gap-3'}`}>
          <img
            src={collapsed ? logo : logoSmall}
            alt="BeanGo"
            className={collapsed ? 'h-8 w-auto mx-auto' : 'h-8 w-auto'}
          />
        </div>
      </div>

      {/* Organization Name - clickable to settings */}
      {!collapsed && (
        <div className="border-b border-[var(--color-sidebar-border)] px-4 py-3">
          <div className="text-xs font-semibold uppercase tracking-wider mb-1" style={{ color: 'var(--color-sidebar-foreground)', opacity: 0.5 }}>
            Organization
          </div>
          {organization ? (
            <NavLink
              to={`/app/organizations/${organization.id}/settings/members`}
              className="font-medium truncate block hover:underline transition-colors"
              style={{ color: 'var(--color-sidebar-foreground)' }}
            >
              {organization.name}
            </NavLink>
          ) : (
            <div className="animate-pulse h-4 bg-slate-200/50 rounded w-3/4" />
          )}
        </div>
      )}

      {/* Navigation */}
      {isSettingsPage ? (
        <SettingsSubNav collapsed={collapsed} orgId={organization?.id} />
      ) : (
        <nav className="flex-1 overflow-y-auto py-4">
          {navSections.map((section, sectionIndex) => {
            const hasTitle = !!section.title;
            const isExpanded = expandedSection === sectionIndex;

            return (
              <div key={section.title ?? 'main'} className="mb-4">
                {hasTitle && !collapsed ? (
                  <button
                    type="button"
                    onClick={() => toggleSection(sectionIndex)}
                    className="w-full flex items-center justify-between px-4 py-2 text-xs font-semibold uppercase tracking-wider hover:bg-[var(--color-sidebar-accent)] transition-colors cursor-pointer"
                    style={{ color: 'var(--color-sidebar-foreground)', opacity: 0.5 }}
                  >
                    <span>{section.title}</span>
                    <i className={`bi ${isExpanded ? 'bi-chevron-down' : 'bi-chevron-right'} text-[10px]`}></i>
                  </button>
                ) : hasTitle && collapsed ? (
                  <div className="px-4 py-2" />
                ) : null}
                {(!hasTitle || isExpanded || collapsed) &&
                  section.items.map((item) => (
                    <NavLinkItem key={item.href} item={item} collapsed={collapsed} />
                  ))}
              </div>
            );
          })}
        </nav>
      )}

      {/* Settings Link (bottom of normal nav) */}
      {!isSettingsPage && (
        <div className="border-t border-[var(--color-sidebar-border)]">
          <NavLink
            to="/app/settings/profile"
            className={({ isActive }) =>
              `flex items-center gap-3 px-4 py-2 mx-2 mt-1 rounded-lg transition-colors ${
                isActive
                  ? 'bg-[var(--color-sidebar-primary)] text-white'
                  : 'hover:bg-[var(--color-sidebar-accent)]'
              } ${collapsed ? 'justify-center' : ''}`
            }
            title={collapsed ? 'Settings' : undefined}
          >
            <i className={`bi bi-gear ${collapsed ? 'text-lg' : ''}`}></i>
            {!collapsed && <span>Settings</span>}
          </NavLink>
        </div>
      )}

      {/* User & Logout */}
      <div className="border-t border-[var(--color-sidebar-border)] px-4 py-3">
        {!collapsed && user && (
          <div className="mb-3 text-sm" style={{ color: 'var(--color-sidebar-foreground)' }}>
            <div className="font-medium truncate">{user.email}</div>
          </div>
        )}
        <button
          type="button"
          onClick={handleSignOut}
          className={`flex items-center gap-3 px-4 py-2 mx-2 rounded-lg transition-colors hover:bg-[var(--color-sidebar-accent)] w-[calc(100%-1rem)] ${collapsed ? 'justify-center' : ''}`}
          title={collapsed ? 'Sign out' : undefined}
        >
          <i className={`bi bi-box-arrow-left ${collapsed ? 'text-lg' : ''}`}></i>
          {!collapsed && <span>Sign out</span>}
        </button>
        {!collapsed && (
          <div className="mt-2 text-xs" style={{ color: 'var(--color-sidebar-foreground)', opacity: 0.5 }}>
            v1.0.0
          </div>
        )}
      </div>
    </div>
  );
}
